<template>
  <div class="search-list">
    <nav id="navigation" class="navigation" :class="classForNavigation">
      <div class="content-fade-in"></div>
      <div class="content-fade-in"></div>

      <template v-for="(section, i) in sectionDescriptions">
        <div
          :key="i"
          :class="{ 'section-header': true, 'is-active': i === activeTab }"
          @click="activeTab = i"
        >
          {{ section.name }}
          <span class="section-counter">
            ({{ numberOfResultsInSection(results, section) }})
          </span>
        </div>
      </template>
    </nav>
    <div class="all-results">
      <div
        v-for="(section, i) in sectionDescriptions"
        :key="i"
        :class="{ 'section-results': true, 'is-active': i === activeTab }"
      >
        <sanitas-search-result
          v-for="(result, index) in results[section.key]"
          :key="index"
          :type="section.type"
          :href="result.url"
          :title="result.title"
          :description="result.description"
          :image-url="result.imageURL"
          :image-alt="result.imageAlt"
          :file-description="result.fileDescription"
        >
        </sanitas-search-result>
      </div>
    </div>
  </div>
</template>

<script>
import { useI36n } from '@jota-one/i36n/src/index.vue2'

import SanitasSearchResult from './SanitasSearchResult'

export default {
  name: 'SanitasSearchList',
  components: { SanitasSearchResult },
  props: {
    results: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { $label } = useI36n()

    return {
      $t: $label,
    }
  },
  data: () => ({
    activeTab: 0,
  }),
  computed: {
    classForNavigation() {
      if (!this.sectionDescriptions || this.sectionDescriptions.length === 0) {
        return 'navigation--empty'
      }

      return ''
    },
    sectionDescriptions() {
      const { results } = this

      const allSections = [
        {
          key: 'products',
          type: 'product',
          name: this.$t('product-section-title'),
        },
        {
          key: 'articles',
          type: 'article',
          name: this.$t('article-section-title'),
        },
        {
          key: 'documents',
          type: 'document',
          name: this.$t('document-section-title'),
        },
      ]

      const visibleSections = []

      for (let i = 0; i < allSections.length; i++) {
        const section = allSections[i]
        const sectionResults = results[section.key]

        if (sectionResults && sectionResults.length > 0) {
          visibleSections.push(section)
        }
      }

      return visibleSections
    },
  },

  methods: {
    numberOfResultsInSection: function (results, section) {
      return results[section.key]?.length || 0
    },
  },
}
</script>

<style lang="postcss" scoped>
.search-list {
  opacity: 1;
  transition: opacity 0.3s;
  &.is-open {
    opacity: 0.1;
    &:before {
      content: '';
      position: absolute;
      display: block;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 3;
    }
  }
}

@media (max-width: 544px) {
  .search-list {
    #navigation {
      flex-direction: column !important;
      overflow-x: hidden !important;
      border-bottom: none !important;
      .section-header {
        border-bottom: none !important;
      }
    }
  }
}

@media (max-width: 1199px) {
  .search-list {
    position: relative;
    overflow: hidden;

    .navigation {
      &--empty {
        border-bottom: none !important;
      }
    }

    #navigation {
      display: flex;
      margin-bottom: var(--spacing-2);
      border-bottom: 1px solid var(--c-secondary-neutral-3);

      .section-header {
        font-weight: 700;
        font-style: normal;
        font-size: 13px;
        line-height: 1.1;
        text-transform: uppercase;
        box-sizing: border-box;
        color: var(--c-secondary-neutral-3);
        padding: 0 var(--spacing-2) 15px var(--spacing-2);
        border-bottom: 4px solid transparent;
        transition: border 0.2s;
        flex: 1;

        text-align: center;
        white-space: nowrap;
        cursor: pointer;
        user-select: none;

        &.is-active {
          color: var(--c-primary-neutral-3);
          border-color: var(--c-primary-neutral-3);
        }
      }

      .content-fade-in,
      .content-fade-out {
        &:after {
          content: '';
          display: block;
          position: absolute;
          top: 0;
          width: 25px;
          height: 20px;
          pointer-events: none;
          background-color: transparent;
          z-index: 1;
        }
      }

      .content-fade-in.is-active:after {
        right: auto;
        left: 0;
        background: linear-gradient(
          to left,
          var(--c-primary-color-1) 100%,
          var(--c-primary-color-1) 0%
        );
      }

      .content-fade-out.is-active:after {
        left: auto;
        right: 0;
        background: linear-gradient(
          to right,
          var(--c-primary-color-1) 100%,
          var(--c-primary-color-1) 0%
        );
      }
    }

    .section-results {
      position: relative;
      left: 100%;
      padding: 0 var(--spacing-2);
      opacity: 0;
      visibility: hidden;
      height: 0;
      transition: all 0.4s ease-out;

      &.is-active {
        height: auto;
        left: 0;
        opacity: 1;
        visibility: visible;
      }
    }
  }
}

@media (--md) {
  .search-list {
    #navigation {
      margin-bottom: 30px;
    }

    .section-results {
      padding: 0;
    }

    &.is-open {
      &:before {
        top: 150px;
      }
    }
  }
}

@media (--xl) {
  .search-list {
    display: flex;
    flex-wrap: wrap;

    #navigation {
      display: flex;
      width: 100%;
      margin-bottom: 20px;

      .section-header {
        font-family: 'TheMix Light', sans-serif;
        font-weight: 300;
        font-style: normal;
        font-size: 18px;
        line-height: 1.1;
        color: var(--c-primary-neutral-3);

        .section-counter {
          font-weight: 300;
          font-style: normal;
          font-size: 16px;
          line-height: 1.25;
          letter-spacing: normal;
          color: var(--c-primary-neutral-3);
        }
      }
    }

    .section-header,
    .section-results {
      display: inline-block;
      width: 100%;
      box-sizing: border-box;
      padding-right: var(--spacing-6);

      &:last-of-type {
        padding-right: 0;
      }
    }

    .all-results {
      width: 100%;
      display: flex;
    }
  }
}
</style>
